/**
 * 검색엔진·공유 미리보기용 머리말 — **웹 빌드와 API가 같은 것을 쓴다.**
 *
 * 머리말을 만드는 곳이 둘이다:
 *   - 웹 빌드: 언어별 고정 화면(`/en`, `/ko/privacy`…)의 HTML을 미리 찍는다
 *   - API: 프로필(`/@junho`)마다 그 사람의 이름·소개가 든 HTML을 내려준다
 *
 * 둘이 따로 적으면 한쪽만 고쳐져 같은 서비스가 검색 결과에서 두 말을 한다.
 *
 * ## 왜 HTML에 직접 넣는가
 *
 * 네이버·다음·빙 일부와 카카오톡·슬랙 같은 수집기는 **자바스크립트를 안
 * 돌린다.** SPA가 화면을 그린 뒤에 바꾼 제목은 그쪽에 영영 안 보인다 —
 * 받은 HTML에 처음부터 들어 있어야 한다.
 *
 * ## 갈아 끼우는 자리
 *
 * `index.html`의 `<!-- seo:start -->`부터 `<!-- seo:end -->`까지를 통째로
 * 바꾼다. 태그를 하나씩 찾아 고치면 하나가 빠졌을 때 옛 값이 조용히 남는다.
 */

import { LANGUAGE_TAGS, SUPPORTED_LANGUAGES, type Language } from './language.js';

export const SITE_ORIGIN = 'https://life-timeline.com';

/** 언어마다 다른 서비스 소개. 고정 화면의 머리말이 이것을 쓴다 */
export interface SiteCopy {
  /** `og:site_name`. 영어권은 무엇을 하는 곳인지 읽히는 이름을 쓴다 */
  siteName: string;
  /** 첫 화면의 탭 제목 — 화면의 큰 문구(`landing.title`)와 같은 말이어야 한다 */
  title: string;
  /** ⚠️ 160자를 넘기면 구글이 잘라서 내보낸다. 자를 거면 우리가 자른다 */
  description: string;
  privacyTitle: string;
  /** `og:locale` */
  locale: string;
}

export const SITE_COPY: Record<Language, SiteCopy> = {
  ko: {
    siteName: '라타라',
    title: '라타라 — 내 인생을 한 장에',
    description:
      '내 인생을 연도별 타임라인으로 정리하고, 좋아하는 것들을 한곳에 모아 보세요. 링크 하나로 보여주고, 이미지 한 장으로 공유할 수 있어요.',
    privacyTitle: '개인정보처리방침 — 라타라',
    locale: 'ko_KR',
  },
  en: {
    siteName: 'Life TimeLine',
    title: 'Life TimeLine — Your life on one page',
    description:
      'Map out your life year by year and gather your favorite things in one place. Show it with a single link, or share it as one image.',
    privacyTitle: 'Privacy — Life TimeLine',
    locale: 'en_US',
  },
  ja: {
    siteName: 'Life TimeLine',
    title: 'Life TimeLine — あなたの人生を一枚に',
    description:
      'あなたの人生を一年ずつ記録し、好きなものをひとつの場所に集めましょう。リンクひとつで見せたり、一枚の画像で共有したりできます。',
    privacyTitle: 'プライバシーポリシー — Life TimeLine',
    locale: 'ja_JP',
  },
  'zh-cn': {
    siteName: 'Life TimeLine',
    title: 'Life TimeLine — 一页写下你的人生',
    description:
      '按年份梳理你的人生，把喜欢的东西收集在一处。用一个链接展示给别人，或分享成一张图片。',
    privacyTitle: '隐私政策 — Life TimeLine',
    locale: 'zh_CN',
  },
  'zh-tw': {
    siteName: 'Life TimeLine',
    title: 'Life TimeLine — 把人生收進一頁',
    description:
      '按年份整理你的人生時間軸，把喜歡的東西收在同一個地方。用一個連結就能展示，也能存成一張圖片分享。',
    privacyTitle: '隱私權政策 — Life TimeLine',
    locale: 'zh_TW',
  },
  es: {
    siteName: 'Life TimeLine',
    title: 'Life TimeLine — Tu vida en una página',
    description:
      'Ordena tu vida año por año y reúne tus cosas favoritas en un solo lugar. Muéstrala con un solo enlace o compártela en una imagen.',
    privacyTitle: 'Privacidad — Life TimeLine',
    locale: 'es_ES',
  },
  pt: {
    siteName: 'Life TimeLine',
    title: 'Life TimeLine — Sua vida em uma página',
    description:
      'Organize sua vida ano a ano e reúna suas coisas favoritas em um só lugar. Mostre com um único link ou compartilhe como uma imagem.',
    privacyTitle: 'Privacidade — Life TimeLine',
    locale: 'pt_BR',
  },
};

/**
 * 언어별 주소를 갖는 고정 화면. `/{lang}` 뒤에 붙는 부분이다.
 *
 * 프로필은 **여기 없다.** 프로필은 그 사람이 쓴 글이라 언어가 하나다 —
 * 언어마다 주소를 나누면 화면 문구만 다른 같은 문서가 둘이 된다.
 */
export const LOCALIZED_PAGES = ['', '/privacy'] as const;
export type LocalizedPage = (typeof LOCALIZED_PAGES)[number];

/** `/en`, `/ko/privacy` */
export function localizedPath(language: Language, page: LocalizedPage): string {
  return `/${language}${page}`;
}

/**
 * 주소에서 언어 접두어를 읽는다. `/ko/privacy` → `{ language: 'ko', page: '/privacy' }`.
 * 언어별 화면이 아니면 `null`.
 */
export function parseLocalizedPath(
  pathname: string,
): { language: Language; page: LocalizedPage } | null {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  for (const language of SUPPORTED_LANGUAGES) {
    for (const page of LOCALIZED_PAGES) {
      if (trimmed === localizedPath(language, page)) {
        return { language, page };
      }
    }
  }
  return null;
}

export interface Alternate {
  /** `ko`, `en`, `zh-TW`, `x-default` */
  hreflang: string;
  path: string;
}

/**
 * 한 화면의 언어별 짝. **모든 언어판이 같은 목록을 단다** — 구글은 서로
 * 가리켜야(양방향) 짝으로 인정한다.
 *
 * `x-default`는 영어판이다. 접두어 없는 주소(`/`)는 들어오는 순간 언어판으로
 * 보내지므로 그 자체로는 문서가 아니다.
 */
export function pageAlternates(page: LocalizedPage): Alternate[] {
  return [
    ...SUPPORTED_LANGUAGES.map((language) => ({
      hreflang: LANGUAGE_TAGS[language],
      path: localizedPath(language, page),
    })),
    { hreflang: 'x-default', path: localizedPath('en', page) },
  ];
}

export interface HeadTags {
  lang: Language;
  title: string;
  description: string;
  /** 정본 주소. `?lang=`·`?tab=`을 뗀 경로만 */
  canonicalPath: string;
  siteName: string;
  locale: string;
  /** 다른 언어판의 `og:locale:alternate` */
  alternateLocales?: string[];
  alternates?: Alternate[];
  ogType?: 'website' | 'profile';
  /** 절대 주소. 없으면 서비스 공통 그림 */
  image?: string;
  /** `noindex` 같은 값. 없으면 태그를 안 단다 */
  robots?: string;
  jsonLd?: Record<string, unknown>;
}

const DEFAULT_IMAGE = `${SITE_ORIGIN}/og-image.png`;

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * JSON-LD를 `<script>` 안에 넣을 수 있게 만든다.
 *
 * ⚠️ 사람이 쓴 소개에 `</script>`가 들어 있으면 거기서 스크립트가 끝나고
 * 나머지가 HTML로 해석된다. `<`를 유니코드 이스케이프로 바꿔 막는다 —
 * JSON으로는 같은 값이다.
 */
function safeJson(value: unknown): string {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function absolute(path: string): string {
  return `${SITE_ORIGIN}${path}`;
}

export function renderHead(tags: HeadTags): string {
  const meta = (attr: 'name' | 'property', key: string, value: string) =>
    `<meta ${attr}="${key}" content="${escapeHtml(value)}" />`;

  const lines = [
    `<title>${escapeHtml(tags.title)}</title>`,
    meta('name', 'description', tags.description),
    ...(tags.robots === undefined ? [] : [meta('name', 'robots', tags.robots)]),
    `<link rel="canonical" href="${escapeHtml(absolute(tags.canonicalPath))}" />`,
    ...(tags.alternates ?? []).map(
      (alternate) =>
        `<link rel="alternate" hreflang="${alternate.hreflang}" href="${escapeHtml(absolute(alternate.path))}" />`,
    ),
    meta('property', 'og:type', tags.ogType ?? 'website'),
    meta('property', 'og:site_name', tags.siteName),
    meta('property', 'og:url', absolute(tags.canonicalPath)),
    meta('property', 'og:title', tags.title),
    meta('property', 'og:description', tags.description),
    meta('property', 'og:image', tags.image ?? DEFAULT_IMAGE),
    meta('property', 'og:locale', tags.locale),
    ...(tags.alternateLocales ?? []).map((locale) =>
      meta('property', 'og:locale:alternate', locale),
    ),
    meta('name', 'twitter:card', 'summary_large_image'),
    ...(tags.jsonLd === undefined
      ? []
      : [`<script type="application/ld+json">${safeJson(tags.jsonLd)}</script>`]),
  ];
  return lines.map((line) => `    ${line}`).join('\n');
}

const REGION = /<!-- seo:start -->[\s\S]*?<!-- seo:end -->/;

/**
 * `index.html`에 머리말을 넣는다. `body`를 주면 `#root` 안에도 넣는다.
 *
 * `#root`에 넣은 것은 **리액트가 뜨면 지워진다**(`createRoot`가 자리를 비우고
 * 그린다). 자바스크립트를 안 돌리는 검색엔진만 읽는 본문이다.
 *
 * @throws 갈아 끼울 자리(`seo:start`/`seo:end`)가 없을 때. 조용히 넘어가면
 *   모든 프로필이 서비스 소개를 단 채 나간다.
 */
export function injectHead(html: string, tags: HeadTags, body?: string): string {
  if (!REGION.test(html)) {
    throw new Error('index.html에 <!-- seo:start --> … <!-- seo:end --> 자리가 없습니다');
  }
  let result = html
    .replace(REGION, () => `<!-- seo:start -->\n${renderHead(tags)}\n    <!-- seo:end -->`)
    .replace(/<html lang="[^"]*"/, `<html lang="${LANGUAGE_TAGS[tags.lang]}"`);
  if (body !== undefined) {
    result = result.replace('<div id="root"></div>', () => `<div id="root">${body}</div>`);
  }
  return result;
}

/** 고정 화면의 머리말. 웹 빌드가 언어마다 찍는다 */
export function localizedPageHead(language: Language, page: LocalizedPage): HeadTags {
  const copy = SITE_COPY[language];
  const path = localizedPath(language, page);
  return {
    lang: language,
    title: page === '/privacy' ? copy.privacyTitle : copy.title,
    description: copy.description,
    canonicalPath: path,
    siteName: copy.siteName,
    locale: copy.locale,
    alternateLocales: SUPPORTED_LANGUAGES.filter((other) => other !== language).map(
      (other) => SITE_COPY[other].locale,
    ),
    alternates: pageAlternates(page),
    // 서비스 자체를 소개하는 정보라 **홈에만** 단다
    ...(page !== '' ? {} : { jsonLd: websiteJsonLd(language, path) }),
  };
}

function websiteJsonLd(language: Language, path: string): Record<string, unknown> {
  const copy = SITE_COPY[language];
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: copy.siteName,
    alternateName: SUPPORTED_LANGUAGES.filter((other) => other !== language).map(
      (other) => SITE_COPY[other].siteName,
    ),
    url: absolute(path),
    description: copy.description,
    inLanguage: LANGUAGE_TAGS[language],
  };
}

/**
 * 프로필 탭 제목. `준호(@junho2343) · Life TimeLine`
 *
 * 이름만으로는 동명이인이 갈리지 않고, 아이디만으로는 누군지 모른다.
 * 둘을 같이 두면 한 줄로 사람이 정해진다.
 */
export function profileDocumentTitle(userName: string, handle: string): string {
  return `${userName}(@${handle}) · ${SITE_COPY.en.siteName}`;
}
